import {
  useState,
  useEffect,
  JSXElementConstructor,
  ReactElement,
  ReactNode,
  ReactPortal,
} from "react";
import {
  CheckPicker,
  Loader,
  Button,
  SelectPicker,
  InputGroup,
  InputNumber,
  RangeSlider,
} from "rsuite";
import upArrow from "../../assets/uparrowFilter.svg";
import downArrow from "../../assets/downarrowFilter.svg";
import { IDemandPayload } from "../../services/interface/IDemand";
import { useDispatch } from "react-redux";
import reload from "../../assets/reload.svg";
import {
  setApplyFilter,
  setBiasFlag,
  setSelectedDBFilter,
  setSelectedFlagMarker,
} from "../../services/redux/reducer/demandfilter.reducer";
import { useLocation } from "react-router-dom";
import moment from "moment";

const DemandForecastFilter = (props: {
  selectedFilterData: IDemandPayload;
  filterSelectionHandler: any;
  removeSelectionHandler: any;
  resFilterData: any;
  filters: any;
  filterHandler: () => void;
  alertDesc: string;
  lastUpdated: string;
  biasFilterRqd?: boolean;
  rangeValue?: number[];
  accuracyValue?: number[];
  setRangeValue?: (value: number[]) => void;
  setAccuracyValue?: (value: number[]) => void;
  setFiltersExpanded?: (value: boolean) => void;
}) => {
  const {
    selectedFilterData,
    alertDesc,
    lastUpdated,
    resFilterData,
    filters,
    filterHandler,
    filterSelectionHandler,
    removeSelectionHandler,
    biasFilterRqd,
    rangeValue,
    accuracyValue,
    setRangeValue,
    setAccuracyValue,
    setFiltersExpanded,
  } = props;
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const dispatch = useDispatch();

  const initialState: IDemandPayload = {
    dc: [],
    storeNumber: [],
    itemId: [],
    filterItemId: [],
    fcwNumber: ["3"],
    ucode: [],
    productSegm: [],
    productDesc: [],
    category: [],
    subCategory: [],
    vendorName: [],
    modelType: ["error based best model"],
    adType: [],
  };

  const filterShowHandler = () => {
    setIsVisible(!isVisible);
  };

  const removeHandler = () => {
    removeSelectionHandler();
    dispatch(setApplyFilter(""));
    dispatch(setSelectedDBFilter(initialState));
  };

  const applyHandler = () => {
    dispatch(setApplyFilter("applied"));
    dispatch(
      setSelectedFlagMarker({ location: [], branchId: "", status: true })
    );
  };

  useEffect(() => {
    dispatch(setSelectedDBFilter(selectedFilterData));
  }, [selectedFilterData]);

  useEffect(() => {
    filterHandler();
  }, []);

  useEffect(() => {
    if (setFiltersExpanded) {
      setFiltersExpanded(isVisible);
    }
  }, [isVisible]);

  return (
    <>
      <div
        className={`flex flex-col gap-1 px-[2%] border-b-[1px] shadow ${
          !isVisible ? "pt-[8px] pb-[2px]" : "pt-[8px] pb-[15px]"
        }`}
      >
        {alertDesc.toLowerCase() != "forecast segmentation" && (
          <div className="flex items-center justify-between mb-1">
            <h3 className="page-title font-semibold">{alertDesc}</h3>
            {
              <div className="flex gap-2">
                <img src={reload} alt="reload" className="w-[1vw]" />
                <p className="text-fs16 text-[#858585] font-semibold">
                  Last Updated :{" "}
                  <span>
                    {moment(lastUpdated?.slice(0, 10)).format("M/DD/YYYY")}
                  </span>
                </p>
              </div>
            }
          </div>
        )}
        <div>
          {isVisible && (
            <div className="grid grid-cols-[1fr_auto] items-end gap-4">
              <div className={`grid grid-cols-12 items-end gap-2`}>
                {filters?.map(
                  (
                    filter: {
                      isSelectPicker: any;
                      title: string;
                      placeholder: string | number | boolean;
                      key: string | number;
                    },
                    index: number
                  ) => {
                    const PickerComponent = filter?.isSelectPicker
                      ? SelectPicker
                      : CheckPicker;
                    const value = filter?.isSelectPicker
                      ? selectedFilterData[
                          filter?.key as keyof typeof selectedFilterData
                        ]?.[0]
                      : selectedFilterData[
                          filter?.key as keyof typeof selectedFilterData
                        ];
                    return (
                      <div className="w-full" key={index}>
                        <p className="filter-header font-semibold">{filter?.title}</p>
                        <PickerComponent
                          size="xs"
                          virtualized
                          block
                          placeholder={filter?.placeholder}
                          data={loading ? [] : resFilterData[filter?.key]}
                          loading={loading}
                          disabled={loading}
                          labelKey="filterVal"
                          valueKey="filterVal"
                          onChange={(data) => {
                            dispatch(setApplyFilter("selected"));
                            if (filter?.isSelectPicker) {
                              filterSelectionHandler(filter?.key, [data]);
                            } else {
                              filterSelectionHandler(filter?.key, data);
                            }
                          }}
                          onOpen={filterHandler}
                          value={value}
                        />
                      </div>
                    );
                  }
                )}
                {biasFilterRqd && (
                  <div className="w-full" key={"bias"}>
                    <p className="filter-header font-semibold">Bias Flag</p>
                    <SelectPicker
                      size="xs"
                      virtualized
                      block
                      placeholder={"All"}
                      data={[
                        { label: "In Range", value: "in" },
                        { label: "Not In Range", value: "out" },
                      ]}
                      loading={loading}
                      disabled={loading}
                      labelKey="label"
                      valueKey="value"
                      onChange={(data) => {
                        dispatch(setApplyFilter("selected"));
                        dispatch(setBiasFlag(data));
                      }}
                    />
                  </div>
                )}
                {rangeValue && setRangeValue && (
                  <div className="w-full" key={"fill-rate"}>
                    <p className="filter-header font-semibold">Fill Rate</p>
                    <div className="flex items-center justify-between">
                      <div className="border-[0.1vh] border-[#BEBEBE] px-[1vh] rounded-[0.75vh] min-w-[5vh] text-center text-fs16 bg-[#F1F1F1]">
                        {rangeValue[0]}
                      </div>

                      <RangeSlider
                        progress
                        style={{
                          width: "5vw",
                          minWidth: "5vw",
                          margin: "0 1vh",
                        }}
                        step={1}
                        tooltip={false}
                        value={[rangeValue[0], rangeValue[1]]}
                        constraint={([start, end]) => start < end}
                        onChange={(value) => {
                          dispatch(setApplyFilter("selected"));
                          setRangeValue(value);
                        }}
                      />

                      <div className="border-[0.1vh] border-[#BEBEBE] px-[1vh] rounded-[0.75vh] min-w-[5vh] text-center text-fs16 bg-[#F1F1F1]">
                        {rangeValue[1]}
                      </div>
                    </div>
                  </div>
                )}

                {accuracyValue && setAccuracyValue && (
                  <div className="w-full ml-[3.75vw]" key={"accuracy"}>
                    <p className="filter-header font-semibold">Accuracy</p>
                    <div className="flex items-center justify-between">
                      <div className="border-[0.1vh] border-[#BEBEBE] px-[1vh] rounded-[0.75vh] min-w-[5vh] text-center text-fs16 bg-[#F1F1F1]">
                        {accuracyValue[0]}
                      </div>

                      <RangeSlider
                        progress
                        style={{
                          width: "5vw",
                          minWidth: "5vw",
                          margin: "0 1vh",
                        }}
                        step={1}
                        tooltip={false}
                        value={[accuracyValue[0], accuracyValue[1]]}
                        constraint={([start, end]) => start < end}
                        onChange={(value) => {
                          dispatch(setApplyFilter("selected"));
                          setAccuracyValue(value);
                        }}
                      />

                      <div className="border-[0.1vh] border-[#BEBEBE] px-[1vh] rounded-[0.75vh] min-w-[5vh] text-center text-fs16 bg-[#F1F1F1]">
                        {accuracyValue[1]}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="align-right flex gap-1">
                {[
                  "/ForecastDeepDive",
                  "/FeatureImportance",
                  "/ForecastOverview",
                  "/ForecastSegmentation",
                  "/PosVsShipped",
                ].includes(location.pathname) && (
                  <Button
                    onClick={removeHandler}
                    className="bg-[#949494] text-white px-[20px] h-[4vh] rounded font-semibold btn-text"
                  >
                    Clear
                  </Button>
                )}
                <Button
                  onClick={applyHandler}
                  className="bg-deloitteGreen text-white px-[20px] h-[4vh] rounded font-semibold btn-text"
                >
                  Apply
                </Button>
              </div>
            </div>
          )}
        </div>
        <div
          className="flex justify-center absolute left-2/4 top-full translate-x-[-50%] translate-y-[-50%] cursor-pointer"
          onClick={filterShowHandler}
        >
          {isVisible && (
            <img className="w-[1.5vw]" src={upArrow} alt="upArrow" />
          )}
          {!isVisible && (
            <img className="w-[1.5vw]" src={downArrow} alt="downArrow" />
          )}
        </div>
      </div>
    </>
  );
};

export default DemandForecastFilter;
